import { FC, PropsWithChildren, useEffect, useState } from 'react';

declare global {
  interface Window {
    UC_UI?: {
      isInitialized: () => boolean;
      isConsentRequired: () => boolean | null;
      showSecondLayer: () => void;
    };
  }
  interface WindowEventMap {
    UC_UI_CMP_EVENT: CustomEvent<{ type: string }>;
    UC_UI_INITIALIZED: Event;
  }
}

const OVERLAY_STYLES = `
  @keyframes cg-spin { to { transform: rotate(360deg) } }
  @keyframes cg-fade { to { opacity: 0 } }
`;

const CONSENT_DECISION_TYPES: ReadonlySet<string> = new Set(['ACCEPT_ALL', 'DENY_ALL', 'SAVE']);

type Phase = 'waiting' | 'fading' | 'done';

/**
 * Blocks all children until the user has acted on the Usercentrics consent banner
 * (Accept All / Deny All / Save). Returning users whose consent is already stored
 * pass through synchronously. There is NO timeout — if UC fails to load, the app
 * remains blocked (the UC script is a hard dependency).
 *
 * Three render phases:
 *  1. waiting   — spinner only (UC banner sits on top in its own stacking context)
 *  2. fading    — children mount + spinner fades out via CSS, bridging the gap
 *                 between the banner closing and Auth0 finishing its redirect
 *  3. done      — children only (fast path for returning users; also final state)
 */
export const ConsentGateProvider: FC<PropsWithChildren<object>> = ({ children }) => {
  const [phase, setPhase] = useState<Phase>(() => {
    if (window.UC_UI?.isInitialized?.()) {
      return window.UC_UI.isConsentRequired() === false ? 'done' : 'waiting';
    }
    return 'waiting';
  });

  useEffect(() => {
    if (phase === 'waiting') {
      // Guards against double-resolve (e.g. both handlers fire before cleanup runs).
      let resolved = false;

      const resolve = () => {
        if (resolved) return;
        resolved = true;
        setPhase('fading');
      };

      // Fires when UC finishes initializing — check if consent is even needed.
      const handleUCInitialized = () => {
        if (window.UC_UI?.isConsentRequired?.() === false) {
          resolve();
        }
        // Otherwise: banner is visible, wait for user action via UC_UI_CMP_EVENT.
      };

      // Only resolve on an actual consent decision — not on banner navigation events
      // (e.g. "More information" / second layer). UC_UI_CMP_EVENT fires for all CMP
      // interactions; event.detail.type distinguishes decisions from navigation.
      const handleConsentDecision = (event: CustomEvent<{ type: string }>) => {
        if (CONSENT_DECISION_TYPES.has(event.detail.type)) {
          resolve();
        }
      };
      window.addEventListener('UC_UI_CMP_EVENT', handleConsentDecision);

      // Check if UC already initialized (race: script loaded before React mounted)
      if (window.UC_UI?.isInitialized?.()) {
        handleUCInitialized();
      } else {
        window.addEventListener('UC_UI_INITIALIZED', handleUCInitialized, { once: true });
      }

      return () => {
        resolved = true;
        window.removeEventListener('UC_UI_CMP_EVENT', handleConsentDecision);
        window.removeEventListener('UC_UI_INITIALIZED', handleUCInitialized);
      };
    }
    return undefined;
  }, [phase]);

  // Phase 1: waiting — static spinner behind the UC banner (zIndex: 1 so UC stays on top)
  if (phase === 'waiting')
    return (
      <>
        <style>{OVERLAY_STYLES}</style>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '4px solid #e0e5e8',
              borderTopColor: '#0271e0',
              animation: 'cg-spin 0.75s linear infinite',
            }}
          />
        </div>
      </>
    );

  // Phase 2: fading — children mount underneath; spinner fades out on top.
  // onAnimationEnd removes the overlay once the CSS animation completes.
  if (phase === 'fading')
    return (
      <>
        {children}
        <style>{OVERLAY_STYLES}</style>
        <div
          onAnimationEnd={() => setPhase('done')}
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            zIndex: 9999,
            animation: 'cg-fade 0.5s ease-out forwards',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '4px solid #e0e5e8',
              borderTopColor: '#0271e0',
              animation: 'cg-spin 0.75s linear infinite',
            }}
          />
        </div>
      </>
    );

  // Phase 3: done — returning users land here directly, all others after fade completes
  return <>{children}</>;
};
